import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { authUserService, type SelfUpdatePayload } from '@/api/services/authUser.service'
import type { AuthUser, CurrentUserResponse } from '@/types/auth.types'
import type { AccessLevel } from '@/types/auth.types'

const VALID_ACCESS_LEVELS: AccessLevel[] = [
  'access_level.admin',
  'access_level.manager',
  'access_level.user',
  'access_level.partner',
]

/**
 * Refresh the signed avatar URL this many ms before its server-stamped
 * expiry. Wide enough to absorb clock skew + a slow network round-trip
 * without leaving a window in which the cached URL is already invalid.
 */
const AVATAR_REFRESH_SAFETY_MS = 30_000

function toAccessLevel(value: string | null): AccessLevel {
  return VALID_ACCESS_LEVELS.includes(value as AccessLevel)
    ? (value as AccessLevel)
    : 'access_level.user'
}

/**
 * Display-name fallback chain:
 *   username → first_name+last_name (legacy PersonalDetailInfo) → email local-part
 * Guarantees a non-empty label for every authenticated user.
 */
function deriveDisplayName(raw: CurrentUserResponse): string {
  if (raw.username && raw.username.trim() !== '') return raw.username
  const d = raw.current_detail
  const first = (d?.first_name ?? '').trim()
  const last = (d?.last_name ?? '').trim()
  const fullName = `${first} ${last}`.trim()
  if (fullName !== '') return fullName
  return raw.email.split('@')[0] ?? raw.email
}

export const useCurrentUserStore = defineStore('currentUser', () => {
  const user = ref<CurrentUserResponse | null>(null)
  const avatarUrl = ref<string | null>(null)
  const avatarUrlExpiresAt = ref<number | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  let avatarRefreshTimer: ReturnType<typeof setTimeout> | null = null

  function clearAvatarRefreshTimer() {
    if (avatarRefreshTimer !== null) {
      clearTimeout(avatarRefreshTimer)
      avatarRefreshTimer = null
    }
  }

  function scheduleAvatarRefresh(expiresAtMs: number) {
    clearAvatarRefreshTimer()
    const delay = Math.max(1_000, expiresAtMs - Date.now() - AVATAR_REFRESH_SAFETY_MS)
    avatarRefreshTimer = setTimeout(() => {
      avatarRefreshTimer = null
      // Skip if the avatar was removed (or the session reset) while we slept —
      // a refresh here would either 404 or resurrect a stale URL.
      if (user.value?.avatar_id == null) return
      void refreshAvatarUrl().catch(() => {
        avatarUrl.value = null
        avatarUrlExpiresAt.value = null
      })
    }, delay)
  }

  const detail = computed(() => user.value?.current_detail ?? null)

  const authUser = computed<AuthUser | null>(() => {
    if (!user.value) return null
    const raw = user.value
    const d = raw.current_detail
    return {
      id: raw.id,
      email: raw.email,
      username: raw.username,
      first_name: d?.first_name ?? '',
      last_name: d?.last_name ?? '',
      access_level: toAccessLevel(raw.access_level),
      is_leader: raw.is_leader,
      avatar_url: avatarUrl.value,
      has_avatar: raw.avatar_id != null,
      picture_url: d?.picture_url ?? undefined,
      job_title: raw.role_name ?? undefined,
      fiscozen_partner_id: raw.fiscozen_partner_id ?? null,
      impersonated_by: raw.impersonated_by ?? null,
    }
  })

  const displayName = computed<string>(() => (user.value ? deriveDisplayName(user.value) : ''))

  async function fetchCurrentUser() {
    loading.value = true
    error.value = null
    try {
      user.value = await authUserService.getMe()
      if (user.value.avatar_id != null) {
        await refreshAvatarUrl()
      } else {
        avatarUrl.value = null
        avatarUrlExpiresAt.value = null
        clearAvatarRefreshTimer()
      }
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load current user'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function refreshAvatarUrl() {
    const signed = await authUserService.getMyAvatarUrl()
    if (!signed) {
      avatarUrl.value = null
      avatarUrlExpiresAt.value = null
      clearAvatarRefreshTimer()
      return
    }
    avatarUrl.value = signed.download_url
    const expiresMs = Date.parse(signed.download_url_expires_at)
    if (Number.isFinite(expiresMs)) {
      avatarUrlExpiresAt.value = expiresMs
      scheduleAvatarRefresh(expiresMs)
    } else {
      avatarUrlExpiresAt.value = null
      clearAvatarRefreshTimer()
    }
  }

  async function updateProfile(payload: SelfUpdatePayload): Promise<void> {
    user.value = await authUserService.patchMe(payload)
  }

  async function uploadAvatar(file: File): Promise<void> {
    await authUserService.uploadMyAvatar(file)
    await fetchCurrentUser()
  }

  async function deleteAvatar(): Promise<void> {
    await authUserService.deleteMyAvatar()
    avatarUrl.value = null
    avatarUrlExpiresAt.value = null
    clearAvatarRefreshTimer()
    if (user.value) user.value = { ...user.value, avatar_id: null }
  }

  function reset() {
    user.value = null
    avatarUrl.value = null
    avatarUrlExpiresAt.value = null
    error.value = null
    clearAvatarRefreshTimer()
  }

  return {
    user,
    avatarUrl,
    avatarUrlExpiresAt,
    detail,
    authUser,
    displayName,
    loading,
    error,
    fetchCurrentUser,
    refreshAvatarUrl,
    updateProfile,
    uploadAvatar,
    deleteAvatar,
    reset,
  }
})
